import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Radar, Cpu, GitPullRequest, CheckCircle2, Activity, Lock,
  Code2, Cloud, Boxes, KeyRound, FileCheck, ArrowRight, Zap, Search,
  ListOrdered, Wrench, ScanLine, Eye, TerminalSquare,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InteractiveScannerDemo } from "@/components/InteractiveScannerDemo";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const MODULES = [
  { icon: Code2, title: "Application Security", items: ["VAPT", "SAST", "DAST", "Dependencies"], color: "#00F0FF" },
  { icon: Cloud, title: "Infrastructure", items: ["Endpoint", "Cloud Posture", "Configuration"], color: "#3B82F6" },
  { icon: Radar, title: "Threat & Exposure", items: ["Credential Leaks", "Vuln Intel", "Attack Surface"], color: "#FF3366" },
  { icon: FileCheck, title: "Compliance", items: ["SOC 2", "ISO 27001", "GDPR", "HIPAA"], color: "#A855F7" },
];

const STEPS = [
  { n: "01", icon: Search, title: "Discover", desc: "Scanners detect vulnerabilities across code, cloud and endpoints." },
  { n: "02", icon: Cpu, title: "Understand", desc: "The AI analyzes each finding in context — not in isolation." },
  { n: "03", icon: ListOrdered, title: "Prioritize", desc: "Surfaces what needs action now instead of treating findings equally." },
  { n: "04", icon: Wrench, title: "Remediate", desc: "Every finding becomes actionable engineering work with a fix." },
  { n: "05", icon: ScanLine, title: "Validate", desc: "The fix is verified with a re-scan — not just marked done." },
  { n: "06", icon: Eye, title: "Monitor", desc: "Security keeps running so posture doesn't quietly drift." },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-bg text-ink">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 radial-fade" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="bg-cyber/10 text-cyber border border-cyber/30 font-mono text-xs mb-6" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-cyber mr-2 live-dot" /> SECURITY CONSOLE · ONLINE
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Your Autonomous <br />
              <span className="text-cyber text-glow">AI Security Engineer</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted max-w-xl leading-relaxed">
              AICS AGENT helps growing companies discover, understand, prioritize and remediate security
              risks — while building continuous security and compliance readiness. Not a chatbot. An
              intelligent security engineering layer.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="hero-start-btn"
                onClick={() => navigate("/signup")}
                className="bg-cyber text-[#0B0E14] hover:bg-cyber-dim font-semibold h-12 px-6 text-base"
              >
                Start Free — Launch Console <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button
                data-testid="hero-demo-btn"
                variant="outline"
                onClick={() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })}
                className="border-line text-ink hover:border-cyber h-12 px-6 text-base"
              >
                <TerminalSquare size={18} className="mr-2" /> Run Interactive Demo
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[["6", "Security modules"], ["72→94", "Verified uplift"], ["4", "Live scanners"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display font-extrabold text-2xl text-ink">{n}</div>
                  <div className="text-xs text-muted mt-0.5">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual card */}
          <motion.div {...fadeUp} className="relative">
            <div className="card-surface rounded-2xl p-5 glow-cyan">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded bg-crit/15 text-crit text-[10px] font-bold tracking-wider">CRITICAL</span>
                  <span className="text-xs font-mono text-muted">security finding</span>
                </div>
                <Activity size={16} className="text-cyber" />
              </div>
              <h3 className="font-display font-bold text-lg text-ink">Broken Access Control</h3>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between border-b border-line pb-2">
                  <span className="text-muted">Risk</span><span className="text-crit font-semibold">Critical · CVSS 9.1</span>
                </div>
                <div className="flex justify-between border-b border-line pb-2">
                  <span className="text-muted">Affected</span><span className="text-ink">Auth / Authorization</span>
                </div>
                <div className="rounded-xl bg-[var(--surface-2)] border border-line p-3">
                  <div className="flex items-center gap-1.5 text-xs text-cyber font-semibold mb-1.5">
                    <Cpu size={13} /> AI recommendation
                  </div>
                  <p className="text-sm text-ink leading-snug">
                    "Enforce resource-level access checks in the authorization middleware before returning the object."
                  </p>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-secure/10 border border-secure/30 p-3">
                  <span className="text-sm text-secure font-semibold flex items-center gap-1.5">
                    <CheckCircle2 size={15} /> Re-scan after fix
                  </span>
                  <span className="font-display font-extrabold text-secure">72 → 94</span>
                </div>
              </div>
            </div>
            <div className="absolute -inset-1 -z-10 rounded-2xl bg-cyber/10 blur-2xl" />
          </motion.div>
        </div>
      </section>

      {/* PLATFORM MODULES */}
      <section id="platform" className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <motion.div {...fadeUp} className="max-w-3xl">
          <p className="text-cyber font-mono text-xs tracking-widest uppercase mb-3">The Platform</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            One AI security engineering layer. Multiple security workflows.
          </h2>
          <p className="mt-4 text-muted text-base leading-relaxed">
            Bring fragmented security signals into one intelligent workflow — from discovery to remediation
            and continuous validation.
          </p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {MODULES.map((m, i) => (
            <motion.div key={m.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="card-surface card-hover rounded-2xl p-6" data-testid={`module-${i}`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                style={{ background: `${m.color}18` }}>
                <m.icon size={22} style={{ color: m.color }} />
              </div>
              <h3 className="font-display font-semibold text-lg text-ink">{m.title}</h3>
              <ul className="mt-4 space-y-2">
                {m.items.map((it) => (
                  <li key={it} className="flex items-center gap-2 text-sm text-muted">
                    <CheckCircle2 size={14} style={{ color: m.color }} /> {it}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative border-y border-line bg-[var(--surface)]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24">
          <motion.div {...fadeUp} className="max-w-3xl">
            <p className="text-cyber font-mono text-xs tracking-widest uppercase mb-3">How it works</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              From security finding to verified fix
            </h2>
            <p className="mt-4 text-muted text-base">
              See how the AI Security Engineer turns a raw finding into actionable, verifiable engineering work.
            </p>
          </motion.div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}
                className="card-surface rounded-2xl p-6 relative overflow-hidden group card-hover">
                <span className="absolute top-4 right-5 font-display font-extrabold text-4xl text-line group-hover:text-cyber/30 transition-colors">
                  {s.n}
                </span>
                <div className="w-11 h-11 rounded-xl bg-cyber/10 flex items-center justify-center mb-4">
                  <s.icon size={20} className="text-cyber" />
                </div>
                <h3 className="font-display font-semibold text-lg text-ink">{s.title}</h3>
                <p className="mt-2 text-sm text-muted leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO */}
      <section id="demo" className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <motion.div {...fadeUp} className="max-w-3xl mb-10">
          <p className="text-cyber font-mono text-xs tracking-widest uppercase mb-3">Try it live</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Don't just read about it. Run the Security Engineer.
          </h2>
          <p className="mt-4 text-muted text-base">
            Scan a deliberately vulnerable application and watch the system discover, prioritize, remediate
            and validate — no signup required.
          </p>
        </motion.div>
        <motion.div {...fadeUp}>
          <InteractiveScannerDemo />
        </motion.div>
      </section>

      {/* DIFFERENCE */}
      <section className="border-y border-line bg-[var(--surface)]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24">
          <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            More than a security scanner
          </motion.h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <motion.div {...fadeUp} className="card-surface rounded-2xl p-6 opacity-80">
              <h3 className="font-display font-semibold text-ink mb-4 flex items-center gap-2">
                <Boxes size={18} className="text-muted" /> Traditional Security Tool
              </h3>
              <div className="flex flex-wrap gap-2">
                {["Scan", "Findings", "Report", "Developer investigates", "Developer fixes", "Re-scan", "Manual tracking"].map((s) => (
                  <span key={s} className="px-3 py-1.5 rounded-lg bg-[var(--surface-2)] border border-line text-xs text-muted">{s}</span>
                ))}
              </div>
            </motion.div>
            <motion.div {...fadeUp} className="card-surface rounded-2xl p-6 border-cyber/40 glow-cyan">
              <h3 className="font-display font-semibold text-ink mb-4 flex items-center gap-2">
                <ShieldCheck size={18} className="text-cyber" /> AICS AGENT Security Engineer
              </h3>
              <div className="flex flex-wrap gap-2">
                {["Discover", "Understand", "Prioritize", "Explain", "Recommend", "Validate", "Monitor"].map((s) => (
                  <span key={s} className="px-3 py-1.5 rounded-lg bg-cyber/10 border border-cyber/30 text-xs text-cyber">{s}</span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28">
        <motion.div {...fadeUp} className="relative rounded-3xl overflow-hidden card-surface p-10 lg:p-16 text-center">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="absolute inset-0 radial-fade" />
          <div className="relative">
            <Lock className="mx-auto text-cyber mb-6" size={40} />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
              Find the risk. Fix the risk. <span className="text-cyber">Prove the fix.</span>
            </h2>
            <p className="mt-5 text-muted text-base max-w-2xl mx-auto">
              Give your team an AI security engineering capability that continuously turns findings into
              verifiable outcomes. First 10 companies get 3 months on us — no credit card required.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button onClick={() => navigate("/signup")} data-testid="cta-start-btn"
                className="bg-cyber text-[#0B0E14] hover:bg-cyber-dim font-semibold h-12 px-7 text-base">
                Claim Your Free 3 Months <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button variant="outline" onClick={() => navigate("/pricing")} data-testid="cta-pricing-btn"
                className="border-line text-ink hover:border-cyber h-12 px-7 text-base">
                View Pricing
              </Button>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
